/**
 * Venda na Rota (R2) — regras de TELA, puras e testáveis. Nada aqui decide preço, crédito,
 * estoque ou imposto: o servidor confere tudo de novo ao registrar a venda. A tela usa estas
 * contas para orientar o vendedor (saldo da carga, totais, situação da nota) e para montar o
 * pedido comercial impresso.
 */
import {
  FISCAL_ENVIRONMENT_LABEL,
  compareDecimal,
  convertToBase,
  formatMoney,
  formatQuantity,
  fromScaled,
  lineValue,
  subtractMoney,
  sumMoney,
  toScaled,
  type FiscalEnvironment,
  type FiscalLink,
  type LoadDetail,
  type PrintLayout,
  type PrintLine,
  type RouteSaleInput,
  type SalesOrder,
} from "@cafegestor/shared";

// ------------------------------------------------------------------ carrinho
export type CartLine = {
  key: string;
  productId: string;
  productCode: string;
  productName: string;
  unitId: string;
  unitCode: string;
  /** unidades-base em 1 unidade vendida (1 para a unidade-base; ex.: fardo = 10) */
  factor: string;
  baseUnitCode: string;
  /** quantidade canônica ("1.5") na unidade vendida */
  quantity: string;
  /** preço digitado (canônico) ou null = preço da tabela do cliente (o servidor confere) */
  unitPrice: string | null;
  /** preço da tabela do cliente, quando já consultado (só para mostrar o total) */
  listPrice: string | null;
  /** desconto em R$ da linha (canônico) */
  discount: string;
};

/** Quantidade da linha na unidade-base (o que sai da carga). */
export const lineBase = (l: Pick<CartLine, "quantity" | "factor">) =>
  convertToBase(l.quantity, l.factor);

/** Total da linha: quantidade × preço − desconto; null se o preço ainda não é conhecido. */
export function lineTotal(l: CartLine): string | null {
  const price = l.unitPrice ?? l.listPrice;
  if (price === null) return null;
  return subtractMoney(lineValue(l.quantity, price), l.discount);
}

/** Total do carrinho; null enquanto alguma linha não tiver preço conhecido (sem conexão). */
export function cartTotal(lines: CartLine[]): string | null {
  const totals = lines.map(lineTotal);
  if (totals.some((t) => t === null)) return null;
  return sumMoney(totals as string[]);
}

// ------------------------------------------------------------------ saldo da carga
export type LoadBalance = {
  productId: string;
  productCode: string;
  productName: string;
  baseUnitCode: string;
  loaded: string;
  sold: string;
  remaining: string;
};

/** Saldo por produto da carga (carregado − vendido), do detalhe da carga. */
export function balancesOf(detail: Pick<LoadDetail, "settlement">): LoadBalance[] {
  return detail.settlement.products.map((p) => ({
    productId: p.productId,
    productCode: p.productCode,
    productName: p.productName,
    baseUnitCode: p.baseUnitCode,
    loaded: p.loaded,
    sold: p.sold,
    remaining: p.remaining,
  }));
}

/** Itens de vendas guardadas no aparelho e ainda não registradas (saem da mesma carga). */
export type PendingItems = { productId: string; quantityBase: string }[];

/**
 * Quanto ainda dá para vender do produto, na unidade-base: restante da carga − o que já está
 * no carrinho (menos a linha em edição) − vendas guardadas no aparelho ainda não enviadas.
 * Pode ser negativo (o vendedor vê "falta"); o servidor recusa venda acima do saldo da carga.
 */
export function availableBase(
  productId: string,
  balances: LoadBalance[],
  lines: CartLine[],
  pending: PendingItems,
  exceptKey: string | null = null,
): string {
  const remaining = balances.find((b) => b.productId === productId)?.remaining ?? "0";
  let left = toScaled(remaining);
  for (const l of lines) {
    if (l.productId === productId && l.key !== exceptKey) left -= toScaled(lineBase(l));
  }
  for (const p of pending) if (p.productId === productId) left -= toScaled(p.quantityBase);
  return fromScaled(left);
}

/** Mensagem quando a quantidade passa do saldo da carga (venda acima do saldo é bloqueada). */
export function quantityProblem(
  line: Pick<CartLine, "quantity" | "factor" | "baseUnitCode">,
  available: string,
): string | null {
  if (compareDecimal(line.quantity, "0") <= 0) return "Informe a quantidade.";
  if (compareDecimal(lineBase(line), available) > 0) {
    const left = compareDecimal(available, "0") > 0 ? available : "0";
    return `Só há ${formatQuantity(left)} ${line.baseUnitCode} deste produto na carga.`;
  }
  return null;
}

// ------------------------------------------------------------------ documento e envio
/** O que entregar ao cliente ao finalizar. */
export type DocumentChoice = "pedido" | "nfe";

export function routeSaleBody(input: {
  loadId: string;
  customerId: string;
  paymentMethodId: string;
  lines: CartLine[];
  document: DocumentChoice;
  payment: RouteSaleInput["payment"];
  deviceId: string;
  previewHash: string | null;
  notes?: string | null;
}): RouteSaleInput {
  return {
    loadId: input.loadId,
    customerId: input.customerId,
    paymentMethodId: input.paymentMethodId,
    items: input.lines.map((l) => ({
      productId: l.productId,
      unitId: l.unitId,
      quantity: l.quantity,
      unitPrice: l.unitPrice,
      discount: l.discount,
    })),
    payment: input.payment,
    installments: [],
    model: null,
    deviceId: input.deviceId,
    notes: input.notes ?? null,
    // Pedido de Venda: registra a venda sem pedir a nota (pode ser faturada depois, pelo MESMO
    // pedido); NF-e: registra e pede a nota numa operação só
    emit: input.document === "nfe",
    previewHash: input.document === "nfe" ? input.previewHash : null,
  };
}

// ------------------------------------------------------------------ situação fiscal
export type OrderFiscalKey =
  | "registrado"
  | "aguardando_faturamento"
  | "emissao_solicitada"
  | "processando"
  | "autorizada"
  | "rejeitada"
  | "denegada"
  | "cancelada";

export type OrderFiscalState = {
  key: OrderFiscalKey;
  label: string;
  tone: "neutral" | "warn" | "info" | "ok" | "error";
  /** a nota ativa deste pedido (quando há) */
  documentId: string | null;
  /** pode pedir a NF-e agora (pedido confirmado sem nota ativa) */
  canEmit: boolean;
};

/**
 * Situação fiscal do pedido. Um pedido confirmado sem NF-e NÃO é tratado como venda fiscalmente
 * regular: fica "Aguardando faturamento" até a nota ser emitida pelo mesmo pedido.
 */
export function orderFiscalState(
  order: Pick<SalesOrder, "status">,
  links: FiscalLink[] | undefined,
): OrderFiscalState {
  if (order.status !== "confirmado") {
    return {
      key: "registrado",
      label: "Pedido registrado",
      tone: "neutral",
      documentId: null,
      canEmit: false,
    };
  }
  const sale = (links ?? []).filter((l) => l.use === "venda_rota" || l.use === "venda_fabrica");
  const active = sale.find((l) => !["abandonada", "inutilizada"].includes(l.status));
  if (!active) {
    return {
      key: "aguardando_faturamento",
      label: "Aguardando faturamento (sem nota fiscal)",
      tone: "warn",
      documentId: null,
      canEmit: true,
    };
  }
  const nfe = active.model === "65" ? "NFC-e" : "NF-e";
  const base = { documentId: active.documentId, canEmit: false };
  switch (active.status) {
    case "pendente":
      return { ...base, key: "emissao_solicitada", label: "Emissão solicitada", tone: "info" };
    case "processando":
    case "incerta":
    case "contingencia":
      return { ...base, key: "processando", label: `${nfe} em processamento`, tone: "info" };
    case "autorizada":
      return { ...base, key: "autorizada", label: `${nfe} autorizada`, tone: "ok" };
    case "rejeitada":
      return { ...base, key: "rejeitada", label: `${nfe} rejeitada`, tone: "error" };
    case "denegada":
      return { ...base, key: "denegada", label: `${nfe} denegada`, tone: "error" };
    default:
      return { ...base, key: "cancelada", label: `${nfe} cancelada`, tone: "neutral" };
  }
}

// ------------------------------------------------------------------ pedido impresso
const WIDTH = 48;
/** dinheiro sem o símbolo e com espaço comum (as impressoras não têm o espaço especial) */
const brl = (v: string) =>
  formatMoney(v)
    .replace(/^R\$\s*/, "")
    .replace(/[\u00a0\u202f]/g, " ");
const brlFull = (v: string) => formatMoney(v).replace(/[\u00a0\u202f]/g, " ");

function formatDoc(doc: string | null): string {
  if (!doc) return "";
  if (doc.length === 14)
    return doc.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
  if (doc.length === 11) return doc.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
  return doc;
}

/**
 * Pedido de Venda para entregar ao cliente (bobina de 80 mm, 48 colunas; o mesmo layout neutro
 * das notas, enviado à impressora pelo navegador). Identifica com destaque que NÃO tem valor
 * fiscal e não substitui a nota fiscal.
 */
export function orderPrintLayout(
  order: SalesOrder,
  opts: {
    companyName: string;
    reprint: boolean;
    printedAt: Date;
    environment?: FiscalEnvironment | null;
  },
): PrintLayout {
  const lines: PrintLine[] = [];
  const text = (t: string, o: Omit<Extract<PrintLine, { t: "text" }>, "t" | "text"> = {}) =>
    lines.push({ t: "text", text: t, ...o });
  text(opts.companyName, { align: "center", bold: true });
  lines.push({ t: "rule" });
  text("PEDIDO DE VENDA", { align: "center", bold: true, size: 2 });
  text("DOCUMENTO SEM VALOR FISCAL", { align: "center", bold: true });
  text("NÃO SUBSTITUI A NOTA FISCAL", { align: "center", bold: true });
  if (opts.reprint) text("REIMPRESSÃO", { align: "center", bold: true });
  lines.push({ t: "rule" });
  lines.push({
    t: "pair",
    left: `Pedido ${order.number}`,
    right: order.orderDate.split("-").reverse().join("/"),
    bold: true,
  });
  text(`Cliente: ${order.customerName}`);
  if (order.customerDocument) text(`CPF/CNPJ: ${formatDoc(order.customerDocument)}`);
  if (order.salespersonName) text(`Vendedor: ${order.salespersonName}`);
  if (order.loadNumber) text(`Carga: ${order.loadNumber}`);
  lines.push({ t: "rule" });
  text("PRODUTO", { bold: true });
  lines.push({ t: "pair", left: "  QTD UN x PREÇO", right: "TOTAL", bold: true });
  for (const it of order.items ?? []) {
    text(`${it.productCode} ${it.productName}`);
    lines.push({
      t: "pair",
      left: `  ${formatQuantity(it.quantity)} ${it.unitCode} x ${brl(it.unitPrice)}`,
      right: brl(it.productsValue),
    });
    if (Number(it.discount) > 0) {
      lines.push({ t: "pair", left: "  Desconto", right: `-${brl(it.discount)}` });
    }
  }
  lines.push({ t: "rule" });
  lines.push({ t: "pair", left: "Produtos", right: brl(order.productsTotal) });
  if (Number(order.discountTotal) > 0)
    lines.push({ t: "pair", left: "Descontos", right: `-${brl(order.discountTotal)}` });
  if (Number(order.freightTotal) > 0)
    lines.push({ t: "pair", left: "Frete", right: brl(order.freightTotal) });
  if (Number(order.otherTotal) > 0)
    lines.push({ t: "pair", left: "Outras despesas", right: brl(order.otherTotal) });
  lines.push({ t: "pair", left: "TOTAL", right: brlFull(order.totalAmount), bold: true });
  if (order.paymentMethodName) {
    text(
      `Pagamento: ${order.paymentMethodName}${order.paymentMode === "titulo" ? " (a prazo)" : order.paymentMode === "a_vista" ? " (à vista)" : ""}`,
    );
  }
  for (const p of order.installments ?? []) {
    lines.push({
      t: "pair",
      left: `  Vence ${p.dueDate.split("-").reverse().join("/")}`,
      right: brl(p.amount),
    });
  }
  lines.push({ t: "rule" });
  text(
    "Este pedido não tem valor fiscal. Quando a operação exigir, a nota fiscal é emitida pelo mesmo pedido.",
  );
  if (opts.environment && opts.environment !== "producao") {
    text(`Sistema em ${FISCAL_ENVIRONMENT_LABEL[opts.environment]}`, { align: "center" });
  }
  lines.push({ t: "feed", lines: 2 });
  text("_".repeat(36), { align: "center" });
  text("Assinatura do cliente", { align: "center" });
  text(
    `Impresso em ${opts.printedAt.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}`,
    { align: "center" },
  );
  lines.push({ t: "feed", lines: 3 });
  return { title: `Pedido ${order.number}`, widthChars: WIDTH, lines };
}
